export interface DashboardSession {
  token: string;
  shopDomain: string;
  username: string;
  authProvider?: 'password' | 'haravan';
}

export interface RewardRow {
  id: string;
  type: 'coupon';
  value: string;
  coupon_code: string | null;
  probability: number;
  remaining_uses: number | null;
}

export interface CouponRow {
  id: string;
  reward_id: string;
  code: string;
  reusable: boolean;
  assigned_lead_id: string | null;
}

export interface HaravanDiscountOption {
  id: string;
  code: string;
  label: string;
  value: number;
  discountType: string;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
}

export interface LeadRow {
  id: string;
  email: string;
  phone: string;
  reward_id: string | null;
  reward_label: string | null;
  coupon_code: string | null;
  created_at: string;
  purchased: boolean;
  purchase_amount: number;
  purchase_order_count: number;
  purchase_last_order_at: string | null;
}

export interface DashboardData {
  shopDomain: string;
  installation: {
    scriptTagInstalled: boolean;
    scriptTagId: string;
    scriptTagSrc: string;
    scopes: string;
    installedAt: string;
    scriptTagError: string;
  };
  plugins: DashboardPlugins;
  campaign: {
    id: string;
    name: string;
    active: boolean;
    wheel_config: WheelConfig;
  };
  rewards: RewardRow[];
  coupons: CouponRow[];
  leads: LeadRow[];
  analytics: {
    impressions: number;
    uniqueImpressions: number;
    clicks: number;
    uniqueClicks: number;
    popupOpens: number;
    formSubmissions: number;
    leads: number;
    spins: number;
    rewardWins: number;
    couponsAssigned: number;
  };
  announcementAnalytics: {
    views: number;
    clicks: number;
    dismisses: number;
  };
}

export interface CustomCursorSettings {
  active: boolean;
  cursorUrl: string;
  fileName: string;
}

export type AnnouncementBarPlacement = 'top' | 'bottom';
export type AnnouncementBarAlignment = 'left' | 'center' | 'right' | 'sliding';
export type AnnouncementBarBehavior = 'fixed' | 'scrollable';
export type AnnouncementBackgroundEffect = 'soft' | 'none' | 'spotlight';
export type AnnouncementBarsDisplayMode = 'stacked' | 'alternate';
export type AnnouncementBarsTransition = 'slide' | 'none' | 'fade';

export interface AnnouncementBarsGlobalSettings {
  allowMultiple: boolean;
  displayMode: AnnouncementBarsDisplayMode;
  rotationSeconds: number;
  transition: AnnouncementBarsTransition;
}

export interface AnnouncementBarSettings {
  id: string;
  active: boolean;
  title: string;
  subtitle: string;
  iconUrl?: string;
  iconFileName?: string;
  backgroundColor: string;
  backgroundEffect: AnnouncementBackgroundEffect;
  textColor: string;
  alignment: AnnouncementBarAlignment;
  behavior: AnnouncementBarBehavior;
  placement: AnnouncementBarPlacement;
  linkUrl?: string;
  openInNewWindow: boolean;
  ctaEnabled: boolean;
  ctaText: string;
  ctaTextColor: string;
  ctaBackgroundColor: string;
  ctaCornerRadius: number;
  ctaBorderSize: number;
  ctaBorderColor: string;
  dismissEnabled: boolean;
  dismissOnce: boolean;
  countdownEnabled: boolean;
  showCountdown: boolean;
  countdownStartsAt?: string;
  countdownEndsAt?: string;
}

export type CurrencyCode = 'USD' | 'EUR' | 'JPY' | 'INR' | 'CNY' | 'IDR' | 'THB' | 'KRW' | 'MYR';
export type CurrencySwitcherPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface CurrencyConverterSettings {
  active: boolean;
  baseCurrency: 'VND';
  targetCurrencies: CurrencyCode[];
  autoConvertByIp: boolean;
  showCurrencySwitcher: boolean;
  showSwitcherOnlyOutsideVietnam: boolean;
  switcherPosition: CurrencySwitcherPosition;
  switcherPrimaryColor: string;
  showCurrencySymbol: boolean;
  showCurrencyCode: boolean;
  ratesUpdatedAt?: string;
  rates: Record<CurrencyCode, number>;
}

export interface HaravanProductOption {
  id: string;
  title: string;
  handle: string;
  productUrl: string;
  image: string;
  description: string;
  variantId: string;
  price: number;
  compareAtPrice: number;
}

export interface ShoppableVideoItem {
  id: string;
  videoUrl: string;
  title: string;
  productId: string;
  variantId?: string;
  productTitle: string;
  productUrl: string;
  productImage?: string;
  productPrice?: number;
  productCompareAtPrice?: number;
  productDescription?: string;
}

export type ShoppablePlacementPreset =
  | 'auto'
  | 'top'
  | 'after-hero'
  | 'before-footer'
  | 'after-product-grid'
  | 'after-product-images'
  | 'after-product-info'
  | 'before-recommendations'
  | 'custom';

export interface ShoppablePlacementSettings {
  preset: ShoppablePlacementPreset;
  customSelector: string;
  customMode?: 'before' | 'after';
}

export interface ShoppablePageSection {
  id: string;
  label: string;
  selector: string;
}

export interface InstallationCheckResult {
  installed: boolean;
  privateStorefront: boolean;
  checkedUrl: string;
  status: number;
  scriptFound: boolean;
  checkedAt: string;
  message: string;
}

export interface ShoppableVideoCarousel {
  id: string;
  active: boolean;
  title: string;
  pageUrl: string;
  cardCornerRadius: number;
  cardBorderSize: number;
  cardBorderColor: string;
  cardPlaceholderColor: string;
  productBarPrimaryColor: string;
  productBarSecondaryColor: string;
  productBarBackgroundColor: string;
  placement: ShoppablePlacementSettings;
  items: ShoppableVideoItem[];
}

export interface ShoppableVideosSettings extends ShoppableVideoCarousel {
  carousels: ShoppableVideoCarousel[];
  activeCarouselId?: string;
}

export interface ProductCountdownItem {
  id: string;
  active: boolean;
  productId: string;
  productTitle: string;
  productUrl: string;
  productImage?: string;
  startsAt: string;
  endsAt: string;
  position: 'right' | 'left' | 'above' | 'below';
  backgroundColor: string;
  textColor: string;
  borderRadius: number;
  borderWidth: number;
  borderColor: string;
}

export interface ProductCountdownSettings {
  items: ProductCountdownItem[];
  activeItemId?: string;
}

export interface DashboardPlugins {
  customCursor: CustomCursorSettings;
  announcementBar: AnnouncementBarSettings;
  announcementBars?: AnnouncementBarSettings[];
  announcementBarSettings?: AnnouncementBarsGlobalSettings;
  currencyConverter: CurrencyConverterSettings;
  shoppableVideos: ShoppableVideosSettings;
  productCountdown: ProductCountdownSettings;
}

export interface DashboardSettings {
  username: string;
  haravanApiKeyConfigured: boolean;
  haravanApiKey: string;
  haravanApiKeyPreview: string;
}

export interface WheelConfig {
  theme?: Record<string, unknown>;
  copy?: Record<string, string>;
  trigger?: Record<string, unknown>;
  form?: Record<string, unknown>;
  segmentColors?: string[];
}

export interface RewardDraft {
  id?: string;
  label: string;
  code: string;
  color: string;
  probability: number;
  reusable: boolean;
  remainingUses: number | null;
}

export interface DashboardAnalyticsWindow {
  from: string;
  to: string;
}

export interface DashboardAnalyticsData {
  analytics: DashboardData['analytics'];
  announcementAnalytics: DashboardData['announcementAnalytics'];
}

const functionsUrl = (
  import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ||
  'https://aajcaughootiepspojey.supabase.co/functions/v1'
).replace(/\/+$/, '');

export async function login(
  shopDomain: string,
  username: string,
  password: string,
): Promise<DashboardSession> {
  return request<DashboardSession>('/dashboard-login', {
    method: 'POST',
    body: JSON.stringify({ shopDomain, username, password }),
  });
}

export async function loginWithHaravanSession(sessionToken: string): Promise<DashboardSession> {
  return request<DashboardSession>('/dashboard-haravan-session', {
    method: 'POST',
    body: JSON.stringify({ sessionToken }),
  });
}

export async function getDashboardData(
  token: string,
  analyticsWindow?: DashboardAnalyticsWindow,
  announcementBarId?: string,
): Promise<DashboardData> {
  const params = new URLSearchParams();
  if (analyticsWindow) {
    params.set('analyticsFrom', analyticsWindow.from);
    params.set('analyticsTo', analyticsWindow.to);
  }
  if (announcementBarId) params.set('announcementBarId', announcementBarId);
  const query = params.toString();

  return request<DashboardData>(`/dashboard-data${query ? `?${query}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function getDashboardAnalytics(
  token: string,
  analyticsWindow?: DashboardAnalyticsWindow,
  announcementBarId?: string,
): Promise<DashboardAnalyticsData> {
  const params = new URLSearchParams();
  if (analyticsWindow) {
    params.set('analyticsFrom', analyticsWindow.from);
    params.set('analyticsTo', analyticsWindow.to);
  }
  if (announcementBarId) params.set('announcementBarId', announcementBarId);
  const query = params.toString();

  return request<DashboardAnalyticsData>(`/dashboard-analytics${query ? `?${query}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function saveDashboardData(
  token: string,
  payload: {
    campaignId: string;
    name: string;
    active: boolean;
    wheelConfig: WheelConfig;
    rewards: RewardDraft[];
  },
): Promise<void> {
  await request('/dashboard-save', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function getDashboardSettings(token: string): Promise<DashboardSettings> {
  return request<DashboardSettings>('/dashboard-settings', {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function saveDashboardSettings(
  token: string,
  payload: {
    currentPassword?: string;
    newPassword?: string;
    haravanApiKey?: string;
  },
): Promise<DashboardSettings> {
  return request<DashboardSettings>('/dashboard-settings', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function requestAccount(payload: {
  storeUrl: string;
  email: string;
  phone: string;
}): Promise<void> {
  await request('/request-account', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function checkOrderConversions(
  token: string,
): Promise<{ checked: number; matched: number; ordersScanned: number }> {
  return request('/dashboard-order-conversions', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function checkWidgetInstallation(token: string): Promise<InstallationCheckResult> {
  return request('/dashboard-installation-check', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function getHaravanDiscounts(
  token: string,
): Promise<{ discounts: HaravanDiscountOption[] }> {
  return request('/dashboard-discounts', {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function saveCustomCursor(
  token: string,
  payload: {
    active: boolean;
    file?: {
      name: string;
      type: string;
      dataUrl: string;
    };
    clearFile?: boolean;
  },
): Promise<CustomCursorSettings> {
  return request('/dashboard-custom-cursor', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function saveAnnouncementBar(
  token: string,
  payload: {
    bars: AnnouncementBarSettings[];
    settings?: AnnouncementBarsGlobalSettings;
    activeBarId?: string;
    iconBarId?: string;
    iconFile?: {
      name: string;
      type: string;
      dataUrl: string;
    };
    clearIcon?: boolean;
  },
): Promise<{
  bars: AnnouncementBarSettings[];
  activeBar: AnnouncementBarSettings;
  settings: AnnouncementBarsGlobalSettings;
}> {
  return request('/dashboard-announcement-bar', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function saveCurrencyConverter(
  token: string,
  payload: CurrencyConverterSettings,
): Promise<CurrencyConverterSettings> {
  return request('/dashboard-currency-converter', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function saveShoppableVideos(
  token: string,
  payload: ShoppableVideosSettings,
): Promise<ShoppableVideosSettings> {
  return request('/dashboard-shoppable-videos', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function saveProductCountdown(
  token: string,
  payload: ProductCountdownSettings,
): Promise<ProductCountdownSettings> {
  return request('/dashboard-product-countdown', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
}

export async function getHaravanProducts(
  token: string,
  query = '',
): Promise<{ products: HaravanProductOption[] }> {
  const params = new URLSearchParams({ products: '1' });
  if (query.trim()) params.set('q', query.trim());

  return request<{ products: HaravanProductOption[] }>(
    `/dashboard-shoppable-videos?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

export async function scanShoppablePageSections(
  token: string,
  pageUrl: string,
): Promise<{ sections: ShoppablePageSection[] }> {
  const params = new URLSearchParams({ sections: '1', pageUrl });

  return request<{ sections: ShoppablePageSection[] }>(
    `/dashboard-shoppable-videos?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${functionsUrl}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...init.headers,
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.message || 'Dashboard request failed.');
  return body as T;
}
